/**
 * LSP-20754: how long has this document been hidden?
 *
 * Browser timers pause while a tab is hidden or the device is suspended, so a stall timer that
 * "should have" fired while the customer was in another app never did. Every `[Upload] S3 PUT …`
 * log line carries `visibilityState` and `wasHiddenMs` (hidden time during that PUT's lifetime) so
 * a Datadog search can separate "the network stalled" from "the tab was asleep" without
 * reconstructing it from timestamp gaps.
 *
 * Telemetry only. Nothing here may throw into a caller and nothing here changes behaviour; the
 * stall SWEEP that acts on visibility lives in `blobPutTransport.ts`.
 */
export type DocumentVisibilityLogState = DocumentVisibilityState | "unknown";

let trackerInstalled = false;
let hiddenSince: number | null = null;
let hiddenMsAccumulated = 0;

function hasDocument(): boolean {
	return typeof document !== "undefined" && document !== null;
}

export function readDocumentVisibilityState(): DocumentVisibilityLogState {
	try {
		return hasDocument() ? document.visibilityState : "unknown";
	} catch {
		return "unknown";
	}
}

function onDocumentVisibilityChange(): void {
	const now = Date.now();
	const visibilityState = readDocumentVisibilityState();
	if (visibilityState === "hidden") {
		// A repeated `hidden` must not restart the interval, or the total under-counts.
		if (hiddenSince === null) hiddenSince = now;
		return;
	}
	if (visibilityState === "visible" && hiddenSince !== null) {
		hiddenMsAccumulated += Math.max(0, now - hiddenSince);
		hiddenSince = null;
	}
}

/** Idempotent. Installed lazily by the first PUT so hosts need no wiring to get the log fields. */
export function ensureDocumentVisibilityTracker(): void {
	if (trackerInstalled || !hasDocument()) return;
	try {
		document.addEventListener("visibilitychange", onDocumentVisibilityChange);
	} catch {
		// Telemetry setup must never prevent the upload that asked for it.
		return;
	}
	trackerInstalled = true;
	if (readDocumentVisibilityState() === "hidden") hiddenSince = Date.now();
}

/** Cumulative hidden milliseconds since install, including an interval that is still open. */
export function hiddenMsSoFar(now: number = Date.now()): number {
	return hiddenMsAccumulated + (hiddenSince === null ? 0 : Math.max(0, now - hiddenSince));
}

/** Test seam: detach the listener and zero the counters between specs. Not for production code. */
export function resetDocumentVisibilityTrackerForTests(): void {
	if (trackerInstalled && hasDocument()) document.removeEventListener("visibilitychange", onDocumentVisibilityChange);
	trackerInstalled = false;
	hiddenSince = null;
	hiddenMsAccumulated = 0;
}
